import { Quiz } from "@/types/quiz";
import { QuizCard } from "./QuizCard";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";

interface QuizListProps {
  quizzes: Quiz[] | null;
  isLoading?: boolean;
  onQuizzesChange?: (options?: RefetchOptions) => Promise<QueryObserverResult<Quiz[], Error>>;
  onEdit?: (quiz: Quiz) => void;
  onQuizClick?: (quizId: string) => void;
}

export const QuizList = ({ quizzes, isLoading, onQuizzesChange, onEdit, onQuizClick }: QuizListProps) => {
  const { toast } = useToast();

  const { data: isAdmin } = useQuery({
    queryKey: ["isAdmin"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { data: role } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .maybeSingle();

      return !!role;
    },
  });

  const handleDelete = async (quiz: Quiz) => {
    try {
      const { error } = await supabase
        .from("quizzes")
        .delete()
        .eq("id", quiz.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Quiz deleted successfully",
      });

      onQuizzesChange?.();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return <div className="text-center py-4">Loading quizzes...</div>;
  }

  if (!quizzes || quizzes.length === 0) {
    return (
      <div className="text-center py-4">
        No quizzes available for this level yet.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {quizzes.map((quiz) => (
        <QuizCard
          key={quiz.id}
          quiz={quiz}
          isAdmin={isAdmin}
          onEdit={onEdit}
          onDelete={handleDelete}
          onClick={() => onQuizClick?.(quiz.id)}
        />
      ))}
    </div>
  );
};